import { useEffect, useState } from "react";
import { AlertCircle, Check, RotateCcw, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type {
  AIAutofillPhase,
  AIAutofillProgress as AutofillProgressState,
} from "@/hooks/useAIReconSuggestions";

type StepId = "prepare" | "upload" | "queue" | "download" | "analyze" | "build";
type StepStatus = "complete" | "active" | "pending" | "error";

/** Pipeline order. `done`/`error` are terminal and handled separately. */
const PHASE_ORDER: Record<Exclude<AIAutofillPhase, "error">, number> = {
  compressing: 0,
  uploading: 1,
  queued: 2,
  downloading: 3,
  analyzing: 4,
  matching: 5,
  done: 6,
};

const STEP_PHASE: Record<StepId, Exclude<AIAutofillPhase, "error" | "done">> = {
  prepare: "compressing",
  upload: "uploading",
  queue: "queued",
  download: "downloading",
  analyze: "analyzing",
  build: "matching",
};

const ANALYZING_CAPTIONS = [
  "Looking for dents, scratches, and paint damage…",
  "Checking tires, wheels, and glass…",
  "Inspecting the interior for wear and stains…",
  "Matching findings to your recon stages…",
  "Estimating how many days each stage needs…",
  "Writing descriptions for your team…",
];
const CAPTION_SECONDS = 4;
const MAX_THUMBNAILS = 10;

const formatElapsed = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/**
 * Seconds ticking once a second while `active`. Server heartbeats (`serverSeconds`)
 * re-anchor the count; without them (fallback POST) it counts from activation.
 */
function useLiveSeconds(active: boolean, serverSeconds: number | undefined): number {
  const key = active ? `on:${serverSeconds ?? "local"}` : "off";
  const [anchor, setAnchor] = useState({ key: "off", base: 0, at: 0 });
  const [now, setNow] = useState(() => Date.now());

  if (anchor.key !== key) {
    // Adjust-state-while-rendering: re-anchor when the source of truth changes.
    const at = Date.now();
    setAnchor({ key, base: serverSeconds ?? 0, at });
    setNow(at);
  }

  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  if (!active) return 0;
  return Math.max(0, Math.floor(anchor.base + (now - anchor.at) / 1000));
}

/** Object URLs for the photo strip, revoked when the files change or on unmount. */
function useObjectUrls(files: File[]): string[] {
  const [urls, setUrls] = useState<string[]>([]);
  useEffect(() => {
    const created = files.map((file) => URL.createObjectURL(file));
    setUrls(created);
    return () => created.forEach((url) => URL.revokeObjectURL(url));
  }, [files]);
  return urls;
}

function overallPercent(progress: AutofillProgressState, elapsed: number): number {
  const total = Math.max(progress.total, 1);
  const phase = progress.phase === "error" ? progress.failedPhase ?? "compressing" : progress.phase;
  switch (phase) {
    case "compressing":
      return 4;
    case "uploading":
      return 6 + 30 * ((progress.uploaded ?? 0) / total);
    case "queued":
      return 38;
    case "downloading":
      return 40 + 12 * ((progress.downloaded ?? 0) / total);
    case "analyzing":
      // Unknown duration: ease toward 92% so the bar never stalls or finishes early.
      return 52 + 40 * (1 - Math.exp(-elapsed / 25));
    case "matching":
      return 96;
    case "done":
      return 100;
  }
}

function statusText(progress: AutofillProgressState): string {
  const { phase, total } = progress;
  const photos = `${total} photo${total !== 1 ? "s" : ""}`;
  switch (phase) {
    case "compressing":
      return `Preparing ${photos}…`;
    case "uploading":
      return `Uploading photos — ${progress.uploaded ?? 0} of ${total}`;
    case "queued":
      return "Waiting for an AI slot…";
    case "downloading":
      return `AI is reading photos — ${progress.downloaded ?? 0} of ${total}`;
    case "analyzing":
      return "Analyzing damage and recon stages…";
    case "matching":
      return "Building your suggestions…";
    case "done":
      return "Suggestions ready";
    case "error":
      return progress.errorMessage || "AI auto-fill failed.";
  }
}

interface StepIndicatorProps {
  status: StepStatus;
}

function StepIndicator({ status }: StepIndicatorProps) {
  return (
    <span className="relative flex size-6 shrink-0 items-center justify-center">
      {status === "active" && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-primary/30 motion-safe:animate-ping"
        />
      )}
      <span
        className={cn(
          "relative flex size-6 items-center justify-center rounded-full transition-colors duration-300",
          status === "complete" && "bg-primary text-primary-foreground",
          status === "active" &&
            "border-2 border-primary bg-background shadow-[0_0_0_4px] shadow-primary/15",
          status === "pending" && "border border-muted-foreground/30 bg-background",
          status === "error" && "bg-destructive text-white"
        )}
      >
        {status === "complete" && (
          <Check
            size={14}
            strokeWidth={3}
            className="motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:fade-in motion-safe:duration-300"
          />
        )}
        {status === "active" && (
          <span className="size-2 rounded-full bg-primary motion-safe:animate-pulse" />
        )}
        {status === "error" && <X size={14} strokeWidth={3} />}
      </span>
    </span>
  );
}

export interface AIAutofillProgressProps {
  progress: AutofillProgressState;
  /** The photos being analyzed (for the thumbnail strip). */
  files: File[];
  onCancel: () => void;
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
}

/** Live, step-by-step progress for AI auto-fill (fed by the SSE stream). */
export function AIAutofillProgress({
  progress,
  files,
  onCancel,
  onRetry,
  onDismiss,
  className,
}: AIAutofillProgressProps) {
  const isError = progress.phase === "error";
  const isDone = progress.phase === "done";
  const effectivePhase =
    progress.phase === "error" ? progress.failedPhase ?? "compressing" : progress.phase;
  const currentIndex = PHASE_ORDER[effectivePhase];
  const timerActive = !isError && (progress.phase === "analyzing" || progress.phase === "queued");
  const elapsed = useLiveSeconds(timerActive, progress.elapsedS);
  const thumbnails = useObjectUrls(files);

  const total = progress.total;
  const uploaded = progress.uploaded ?? 0;
  const downloaded = progress.downloaded ?? 0;
  const captionIndex = Math.floor(elapsed / CAPTION_SECONDS) % ANALYZING_CAPTIONS.length;
  const percent = Math.round(overallPercent(progress, elapsed));

  const stepStatus = (id: StepId): StepStatus => {
    const index = PHASE_ORDER[STEP_PHASE[id]];
    if (index < currentIndex) return "complete";
    if (index === currentIndex) return isError ? "error" : isDone ? "complete" : "active";
    return "pending";
  };

  const steps: { id: StepId; label: string; detail?: string }[] = [
    { id: "prepare", label: "Preparing photos", detail: "Resizing for a fast upload" },
    {
      id: "upload",
      label: "Uploading",
      detail: currentIndex >= PHASE_ORDER.uploading ? `${Math.min(uploaded, total)}/${total}` : undefined,
    },
    ...(progress.wasQueued
      ? [{ id: "queue" as const, label: "Waiting for AI", detail: "Busy right now — you're next in line" }]
      : []),
    {
      id: "download",
      label: "Reading photos",
      detail:
        currentIndex >= PHASE_ORDER.downloading
          ? `${Math.min(downloaded, total)}/${total}${progress.skipped ? ` · ${progress.skipped} skipped` : ""}`
          : undefined,
    },
    { id: "analyze", label: "Analyzing damage & stages" },
    { id: "build", label: "Building suggestions" },
  ];

  const visibleThumbs = thumbnails.slice(0, MAX_THUMBNAILS);
  const hiddenCount = files.length - visibleThumbs.length;
  const analyzingOrLater = currentIndex >= PHASE_ORDER.analyzing;

  return (
    <div
      className={cn(
        "mx-auto flex w-full max-w-md flex-col gap-5 py-6 sm:py-8",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300",
        className
      )}
      data-testid="ai-autofill-progress"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-full",
            isError ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary",
            !isError && !isDone && "shadow-[0_0_24px] shadow-primary/30"
          )}
        >
          {isError ? (
            <AlertCircle size={22} />
          ) : isDone ? (
            <Check size={22} className="motion-safe:animate-in motion-safe:zoom-in-50" />
          ) : (
            <Sparkles size={22} className="motion-safe:animate-pulse" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-medium">
            {isError
              ? "AI auto-fill didn't finish"
              : isDone
                ? "Suggestions ready"
                : `AI is analyzing ${total} photo${total !== 1 ? "s" : ""}`}
          </p>
          <p
            role="status"
            aria-live="polite"
            className={cn("truncate text-sm", isError ? "text-destructive" : "text-muted-foreground")}
          >
            {statusText(progress)}
          </p>
        </div>
      </div>

      {/* Overall progress */}
      <Progress
        value={percent}
        aria-label="AI auto-fill progress"
        className={cn(
          "h-1.5 [&>[data-slot=progress-indicator]]:duration-700 [&>[data-slot=progress-indicator]]:ease-out",
          isError && "bg-destructive/20 [&>[data-slot=progress-indicator]]:bg-destructive"
        )}
      />

      {/* Photo strip: dim → uploaded → read by the AI */}
      {visibleThumbs.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Photos being analyzed">
          {visibleThumbs.map((url, i) => {
            const isRead = analyzingOrLater || i < downloaded;
            const isUploaded = currentIndex > PHASE_ORDER.uploading || i < uploaded;
            return (
              <li
                key={url}
                className={cn(
                  "relative size-10 overflow-hidden rounded-md bg-muted transition-all duration-500 sm:size-11",
                  isRead
                    ? "opacity-100 ring-2 ring-primary/70 ring-offset-1 ring-offset-background"
                    : isUploaded
                      ? "opacity-70"
                      : "opacity-35 grayscale"
                )}
              >
                <img src={url} alt={`Photo ${i + 1}`} className="size-full object-cover" />
                {progress.phase === "analyzing" && (
                  <span
                    aria-hidden
                    className="absolute inset-0 bg-[linear-gradient(110deg,transparent_30%,rgb(255_255_255/0.35)_50%,transparent_70%)] bg-[length:200%_100%] motion-safe:animate-ai-shimmer motion-reduce:hidden"
                  />
                )}
              </li>
            );
          })}
          {hiddenCount > 0 && (
            <li className="flex size-10 items-center justify-center rounded-md bg-muted text-xs font-medium text-muted-foreground sm:size-11">
              +{hiddenCount}
            </li>
          )}
        </ul>
      )}

      {/* Step timeline */}
      <ol className="flex flex-col" aria-label="AI auto-fill steps">
        {steps.map((step, i) => {
          const status = stepStatus(step.id);
          const isLast = i === steps.length - 1;
          const isAnalyzeActive = step.id === "analyze" && status === "active";
          return (
            <li
              key={step.id}
              data-status={status}
              aria-current={status === "active" ? "step" : undefined}
              className="relative flex gap-3 pb-4 last:pb-0 motion-safe:animate-in motion-safe:fade-in"
            >
              {!isLast && (
                <span
                  aria-hidden
                  className={cn(
                    "absolute left-3 top-7 bottom-1 w-px -translate-x-1/2 transition-colors duration-500",
                    status === "complete" ? "bg-primary" : "bg-border"
                  )}
                />
              )}
              <StepIndicator status={status} />
              <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span
                    className={cn(
                      "text-sm transition-colors",
                      status === "pending" && "text-muted-foreground",
                      status === "active" && "font-medium text-foreground",
                      status === "complete" && "text-foreground",
                      status === "error" && "font-medium text-destructive",
                      isAnalyzeActive &&
                        "motion-safe:bg-[linear-gradient(90deg,var(--foreground)_35%,var(--primary)_50%,var(--foreground)_65%)] motion-safe:bg-[length:200%_100%] motion-safe:bg-clip-text motion-safe:text-transparent motion-safe:animate-ai-shimmer"
                    )}
                  >
                    {step.label}
                  </span>
                  {isAnalyzeActive ? (
                    <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                      {formatElapsed(elapsed)}
                    </span>
                  ) : (
                    step.detail &&
                    status !== "pending" && (
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {step.detail}
                      </span>
                    )
                  )}
                </div>
                {step.id === "queue" && status === "active" && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {step.detail} · {formatElapsed(elapsed)}
                  </p>
                )}
                {isAnalyzeActive && (
                  <p
                    key={captionIndex}
                    className="mt-0.5 text-xs text-muted-foreground motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-500"
                  >
                    {ANALYZING_CAPTIONS[captionIndex]}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Actions */}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {isError ? (
          <>
            {onDismiss && (
              <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
                Fill in manually
              </Button>
            )}
            {onRetry && (
              <Button type="button" size="sm" onClick={onRetry} className="gap-2">
                <RotateCcw size={14} />
                Try again
              </Button>
            )}
          </>
        ) : (
          !isDone && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={progress.phase === "matching"}
              aria-label="Cancel AI auto-fill"
            >
              Cancel
            </Button>
          )
        )}
      </div>
    </div>
  );
}

export default AIAutofillProgress;
